import type { RefObject } from 'react'

import {
    gsap,
    registerGsap,
    ScrollTrigger,
    useGSAP,
    wfEase
} from '@/lib/gsap'

/**
 * Homepage hero scroll choreography — a 1:1 pure-GSAP recreation of the Webflow
 * IX2 "scrolling in view" interaction (a-47, event e-728), reproduced straight
 * from the IX2 engine in webflow.js.
 *
 * Engine behaviour reproduced exactly:
 *
 *   Scroll progress (the engine's element formula):
 *     progress = clamp(viewportH - rect.top, 0, viewportH + rect.height)
 *                / (viewportH + rect.height)
 *   With this interaction's config (startsEntering, no offsets) that is exactly
 *   ScrollTrigger start: 'top bottom' -> end: 'bottom top'.
 *
 *   Smoothing (event smoothing = 50):
 *     factor = max(1 - smoothing/100, 0.01) = 0.5
 *     each animation frame: current += (target - current) * factor
 *   i.e. an exponential lerp of the timeline progress toward the scroll
 *   progress, run on every rAF tick (gsap.ticker), not GSAP's `scrub`.
 *
 *   Keyframes: a property is CLAMPED to its first keyframe before that keyframe
 *   and to its last keyframe after it (it does NOT interpolate from the CSS
 *   base). So each track's initial value is its first keyframe, held. Segments
 *   are linear except the play button (which uses Webflow "ease").
 *
 * Tracks (progress %):
 *   product-phone-demo-image._2  x:  100%  (held 0-42) -> 0            (42-57)
 *   product-phone-demo-image._1  x:  0     (held 0-42) -> -10%         (42-57)
 *   iphone-lock-screen-image     y:  0     (held 0-24) -> -100%        (24-42)
 *   hero-text-holder-1           x:  0 (0-20) -> -150 (29) -> -120 (65) -> 0 (100) px
 *   hero-text-holder-2           x:  0 (0-20) ->  150 (29) ->  120 (65) -> 0 (100) px
 *   hero-text-line               opacity: 1 (held 0-20) -> 0           (20-29)
 *   play-icon-holder             opacity: 0 (held 0-57) -> 1 (57-65, ease)
 *   hero-gradient-box            width:   120% (held 0-57) -> 90%      (57-65)
 *   play-me-holder               opacity: 0 (held 0-65) -> 1           (65-70)
 *   hero-text-holder             opacity: 1 (0-65) -> 0 (70) -> 0 (90) -> 1 (95)
 *
 * Scoped to `.home-hero-container` so the identical class names in the
 * dark-mode section are never affected.
 */
const SMOOTHING = 50
const FACTOR = Math.max(1 - SMOOTHING / 100, 0.01)

export const useHeroScroll = (scope: RefObject<HTMLElement | null>): void => {
    useGSAP(
        () => {
            registerGsap()

            const root = scope.current
            if (!root) return

            const hero = root.querySelector<HTMLElement>('.home-hero-container')
            const trigger = root.querySelector<HTMLElement>('.hero-product-demo')
            if (!hero || !trigger) return

            const find = (selector: string): HTMLElement | null => hero.querySelector<HTMLElement>(selector)

            const appBack = find('.product-phone-demo-image._2')
            const appFront = find('.product-phone-demo-image._1')
            const lockScreen = find('.iphone-lock-screen-image')
            const textHolder1 = find('.hero-text-holder-1')
            const textHolder2 = find('.hero-text-holder-2')
            const textLine = find('.hero-text-line')
            const textHolder = find('.hero-text-holder')
            const playIcon = find('.play-icon-holder')
            const gradientBox = find('.hero-gradient-box')
            const playMe = find('.play-me-holder')

            // Initial states = each track's first keyframe (held), per the engine.
            if (appBack) gsap.set(appBack, { xPercent: 100 })
            if (appFront) gsap.set(appFront, { xPercent: 0 })
            if (lockScreen) gsap.set(lockScreen, { yPercent: 0 })
            if (textHolder1) gsap.set(textHolder1, { x: 0 })
            if (textHolder2) gsap.set(textHolder2, { x: 0 })
            if (playIcon) gsap.set(playIcon, { opacity: 0 })
            if (gradientBox) gsap.set(gradientBox, { width: '120%' })
            if (playMe) gsap.set(playMe, { opacity: 0 })

            // Choreography timeline (linear segments; time === keyframe percent).
            const tl = gsap.timeline({ paused: true, defaults: { ease: 'none' } })

            if (appBack) tl.to(appBack, { xPercent: 0, duration: 15 }, 42)
            if (appFront) tl.to(appFront, { xPercent: -10, duration: 15 }, 42)
            if (lockScreen) tl.to(lockScreen, { yPercent: -100, duration: 18 }, 24)

            if (textHolder1) {
                tl.to(textHolder1, { x: -150, duration: 9 }, 20)
                    .to(textHolder1, { x: -120, duration: 36 }, 29)
                    .to(textHolder1, { x: 0, duration: 35 }, 65)
            }
            if (textHolder2) {
                tl.to(textHolder2, { x: 150, duration: 9 }, 20)
                    .to(textHolder2, { x: 120, duration: 36 }, 29)
                    .to(textHolder2, { x: 0, duration: 35 }, 65)
            }
            if (textLine) tl.to(textLine, { opacity: 0, duration: 9 }, 20)

            // Only the play button uses Webflow "ease" on its 57 -> 65 segment.
            if (playIcon) tl.to(playIcon, { opacity: 1, duration: 8, ease: wfEase('ease') }, 57)
            if (gradientBox) tl.to(gradientBox, { width: '90%', duration: 8 }, 57)
            if (playMe) tl.to(playMe, { opacity: 1, duration: 5 }, 65)
            if (textHolder) {
                tl.to(textHolder, { opacity: 0, duration: 5 }, 65).to(textHolder, { opacity: 1, duration: 5 }, 90)
            }

            // Timeline spans 0..100; drive its progress with the engine's lerp.
            const playhead = { target: 0, current: 0 }

            const tick = (): void => {
                const distance = playhead.target - playhead.current
                if (Math.abs(distance) < 0.0001) {
                    if (playhead.current !== playhead.target) {
                        playhead.current = playhead.target
                        tl.progress(playhead.current)
                    }
                    return
                }
                playhead.current += distance * FACTOR
                tl.progress(playhead.current)
            }

            gsap.ticker.add(tick)

            ScrollTrigger.create({
                trigger,
                start: 'top bottom',
                end: 'bottom top',
                onUpdate: self => {
                    playhead.target = self.progress
                }
            })

            // "Play Me Loop" — the Webflow PAGE_START marquee (IX2 a-79). Two
            // identical text rows scroll left forever; row 1 wraps by +100% at the
            // seam so the strip is gapless. 60s per 100% (Webflow's 60000ms).
            const playRow1 = hero.querySelector<HTMLElement>('.play-me-holder-text._1')
            const playRow2 = hero.querySelector<HTMLElement>('.play-me-holder-text._2')
            if (playRow1 && playRow2) {
                const STEP = 60
                gsap.set([playRow1, playRow2], { xPercent: 0 })
                gsap.timeline({ repeat: -1, defaults: { ease: 'none' } })
                    .to(playRow1, { xPercent: -100, duration: STEP }, 0)
                    .to(playRow2, { xPercent: -100, duration: STEP }, 0)
                    .set(playRow1, { xPercent: 100 }, STEP)
                    .to(playRow1, { xPercent: 0, duration: STEP }, STEP)
                    .to(playRow2, { xPercent: -200, duration: STEP }, STEP)
                    .set(playRow2, { xPercent: 0 }, STEP * 2)
            }

            return () => {
                gsap.ticker.remove(tick)
            }
        },
        { scope }
    )
}
